import { useState, useMemo, type ComponentType } from 'react';
import { tituloEsim } from '@/lib/titulo-esim';
import { useQuery } from '@tanstack/react-query';
import { Loader2, StickyNote, Clock, ChevronDown, CheckCircle2, Phone, FileText, XCircle, Send, UserPlus, Zap, Edit3, Calendar, MessageSquare, ShieldCheck, ExternalLink, Star, Wifi, Fingerprint, ArrowRight, Play, Bell, Mail, Paperclip, ClipboardCheck, Reply, ReplyAll, Eye } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { format, isToday, isYesterday } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { NotaRapida } from '@/components/timeline/NotaRapida';
import { useTimelineUnificada, type TimelineEvent } from '@/hooks/useTimelineUnificada';
import { toBrasilia } from '@/lib/dateUtils';
import { cn } from '@/lib/utils';
import { supabase } from '@/integrations/supabase/client';
import { usePainelPedido } from '@/contexts/PainelPedidoContext';
import { useHasAnyRole } from '@/hooks/useHasRole';
import { EmailClienteDialog, EmailLeituraDialog } from '@/components/email-cliente/EmailClienteDialog';

const API_BASE = (import.meta.env.VITE_SUPABASE_URL ?? 'https://beta.crm-api.primetecia.com.br/api').replace(/\/$/, '');

interface TimelineUnificadaProps {
  cnpj: string | null | undefined;
  clienteId?: string;
  pedidoId?: string;
  triagemId?: string;
  leadId?: string;
  leadNumero?: number | null;
  compact?: boolean;
  /** 61a — layout da aba do modal: grid com trilho direito (nota · filtros · resumo) */
  completa?: boolean;
}

const EVENTS_PER_PAGE = 50;

// ============================================================================
// TIMELINE (61a, 29/08/2026) — fio com cor POR TIPO (exceção aprovada pelo
// Douglas, válida só aqui): o hue claro fica no dot/círculo do ícone; o TEXTO
// do rótulo usa a versão escura (4,5:1+). Dia é marcador NO fio, nota do
// consultor é bolha tingida, e o trilho direito deriva tudo dos eventos.
// ============================================================================

type FamiliaKey = 'nota' | 'ligacao' | 'whatsapp' | 'email' | 'fatura' | 'proposta' | 'tabulacao' | 'tarefa' | 'atendimento' | 'troca_esim' | 'sistema';

const FAMILIAS: Record<FamiliaKey, { label: string; cor: string; corTexto: string }> = {
  troca_esim: { label: 'Troca ESIM', cor: '#6E1626', corTexto: '#6E1626' },
  nota: { label: 'Nota', cor: '#5B0A0D', corTexto: '#5B0A0D' },
  ligacao: { label: 'Ligação', cor: '#9D4459', corTexto: '#9D4459' },
  whatsapp: { label: 'WhatsApp', cor: '#5E9678', corTexto: '#128C7E' },
  // E-MAIL (05/09/2026) — indigo fechado: e o unico hue que nao colide com os
  // bordos das outras familias nem com o petrol da tarefa, entao o e-mail se
  // distingue de nota e de WhatsApp num relance.
  email: { label: 'E-mail', cor: '#46557F', corTexto: '#333F63' },
  fatura: { label: 'Fatura', cor: '#A6764B', corTexto: '#7A5230' },
  proposta: { label: 'Proposta', cor: '#6E1626', corTexto: '#6E1626' },
  tabulacao: { label: 'Tabulação', cor: '#7A2235', corTexto: '#7A2235' },
  // TAREFA (Douglas 04/09) — petrol: é o único hue ausente das outras famílias,
  // então a tarefa se distingue de nota, ligação e proposta num relance.
  tarefa: { label: 'Tarefa', cor: '#1B5E6B', corTexto: '#134851' },
  // ATENDIMENTO (Douglas 17/09/2026) — o resumo do que a Jéssica fechou.
  // Usa o bordô mais fechado da timeline, o mesmo peso da nota:
  // é registro sobre o cliente, e o rótulo próprio já o separa no fio.
  atendimento: { label: 'Atendimento', cor: '#5B0A0D', corTexto: '#5B0A0D' },
  sistema: { label: 'Sistema', cor: '#8A7576', corTexto: '#6B5A4D' },
};

// ⛔ 07/09/2026: o e-mail tem DOIS tipos. Enquanto era so' 'email_recebido' os
// quatro lugares abaixo testavam a string na mao, e quando a acao de ENVIAR
// nasceu o e-mail enviado voltou a cair na familia 'nota' — mesmo bug de 05/09,
// so' que no tipo novo. Agora e' uma pergunta so'.
const ehEmail = (t: string) => t === 'email_recebido' || t === 'email_enviado';

function familiaDo(item: TimelineEvent): FamiliaKey {
  const t = item.tipo || '';
  if (t === 'troca_esim') return 'troca_esim';
  // Chamado é recado entre gente sobre este cliente — mesma família da nota.
  // ⛔ Tem que vir ANTES da regra do 'mensagem' abaixo: o e-mail recebido usa
  // entidade 'mensagem' (para nao quebrar o tipo TimelineEntidade do front) e
  // cairia em 'nota', com rotulo e cor errados.
  if (ehEmail(t)) return 'email';
  if (t === 'atendimento') return 'atendimento';
  if (item.entidade === 'mensagem' || t === 'observacao' || t === 'triagem_nota' || t === 'interno') return 'nota';
  if (t === 'chamado_carteira' || t === 'chamado_carteira_resposta') return 'nota';
  if (t === 'ligacao' || t === 'tentativa_ligacao') return 'ligacao';
  if (t === 'qualidade_whatsapp' || t === 'triagem_whatsapp' || t === 'qualidade_boas_vindas') return 'whatsapp';
  if (t === 'whatsapp') return 'whatsapp';
  if (t === 'fatura' || t.startsWith('qualidade_fatura')) return 'fatura';
  if (t === 'proposta' || t === 'retorno' || t.startsWith('proposta_') || t === 'proposta_bee' || t === 'proposta_valores') return 'proposta';
  if (['tabulacao', 'negado', 'conversao', 'cancelamento', 'recuperacao', 'pedido_ia', 'venda_ia'].includes(t)) return 'tabulacao';
  // 'agenda' é o tipo que a tarefa usa (já estava na allowlist do banco, com 0
  // linhas). O "Agendar retorno" da Carteira usa o mesmo tipo — os dois moram
  // na mesma família, e é isso que se quer.
  if (t === 'agenda') return 'tarefa';
  return 'sistema';
}

const ICONE_TIPO: Record<string, ComponentType<{ className?: string; style?: import('react').CSSProperties }>> = {
  email_recebido: Mail, email_enviado: Send,
  proposta: Send, retorno: Send, negado: XCircle, conversao: CheckCircle2, ligacao: Phone, tentativa_ligacao: Phone,
  pedido_ia: CheckCircle2, venda_ia: CheckCircle2,
  status: ArrowRight, kanban_move: ArrowRight, cancelamento: XCircle, recuperacao: CheckCircle2, transferencia: UserPlus,
  atribuicao: UserPlus, devolucao: UserPlus, observacao: StickyNote, interno: StickyNote,
  chamado_carteira: Bell, chamado_carteira_resposta: CheckCircle2,
  fatura: FileText, criacao: Zap, campo: Edit3, agenda: Calendar, biometria: Fingerprint, sms: MessageSquare,
  qualidade_entrada: ShieldCheck, qualidade_boas_vindas: MessageSquare,
  qualidade_fatura_1: FileText, qualidade_fatura_2: FileText, qualidade_fatura_3: FileText, qualidade_trava: FileText,
  qualidade_whatsapp: MessageSquare, triagem_whatsapp: MessageSquare, triagem_nota: StickyNote,
  atendimento: Star,
  defesa_credito: ShieldCheck,
};

const hexRgba = (hex: string, a: number) => {
  const n = parseInt(hex.slice(1), 16);
  return `rgba(${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}, ${a})`;
};

const rotuloDo = (item: TimelineEvent): string => {
  const t = item.tipo || '';
  if (item.campo === 'ajuste_item') {
    const payload = item.payload as { origem?: unknown } | null;
    return payload?.origem === 'Extrato Claro Control' || item.motivo === 'Conciliação com extrato Claro'
      ? 'Claro Control'
      : 'Correção do pedido';
  }
  if (typeof item.descricao === 'string' && item.descricao.startsWith('Convergência')) return 'Convergência';
  // Os 3 momentos da tarefa são o MESMO tipo ('agenda') com `campo` diferente —
  // assim o filtro "Tarefa" traz a história inteira, e não pedaços dela.
  if (t === 'agenda') {
    const c = String(item.campo || '');
    if (c === 'Tarefa cumprida') return 'Tarefa cumprida';
    if (c === 'Tarefa adiada') return 'Tarefa adiada';
    if (c === 'Retorno') return 'Retorno agendado';
    return 'Tarefa agendada';
  }
  const MAPA: Record<string, string> = {
    troca_esim: 'Troca ESIM',
    proposta: 'Proposta', proposta_bee: 'Proposta Bee', proposta_valores: 'Valores da proposta', retorno: 'Retorno', negado: 'Negado', conversao: 'Convertido',
    ligacao: 'Ligação real', tentativa_ligacao: 'Tentativa de ligação', status: 'Status', kanban_move: 'Movimentação', cancelamento: 'Cancelado',
    recuperacao: 'Recuperado', atribuicao: 'Atribuição', transferencia: 'Troca de carteira', devolucao: 'Devolução', observacao: 'Nota',
    interno: 'Nota', fatura: 'Fatura', criacao: 'Criação', campo: 'Campo', agenda: 'Agenda', sms: 'SMS', contato_real_validado: 'Contato real',
    biometria: 'Biometria', qualidade_entrada: 'Qualidade · entrada', qualidade_boas_vindas: 'Boas-vindas',
    qualidade_fatura_1: '1ª fatura', qualidade_fatura_2: '2ª fatura', qualidade_fatura_3: '3ª fatura',
    qualidade_trava: 'Justificativa da trava',
    qualidade_whatsapp: 'WhatsApp Qualidade', triagem_whatsapp: 'Triagem WhatsApp',
    triagem_recebida: 'Triagem recebida', triagem_assumida: 'Triagem assumida',
    triagem_finalizada: 'Triagem finalizada', triagem_nota: 'Nota da triagem', triagem_atualizada_ia: 'Atualizado pela IA',
    chamado_carteira: 'Chamado', chamado_carteira_resposta: 'Chamado atendido', pedido_ia: 'Pedido pela IA',
    venda_ia: 'Venda fechada pela IA',
    tabulacao: 'Tabulação', whatsapp: 'WhatsApp', atendimento: 'Atendimento',
    defesa_credito: 'Crédito reprovado',
  };
  // ⛔ ANTES da regra do 'mensagem' logo abaixo: o e-mail recebido usa
  // entidade 'mensagem' e sairia rotulado como "Nota".
  if (t === 'email_enviado') return 'E-mail enviado';
  if (t === 'email_recebido') return 'E-mail recebido';
  if (item.entidade === 'mensagem') return 'Nota';
  return MAPA[t] || (t ? t.replace(/_/g, ' ') : 'Evento');
};

const extractLabel = (val: unknown): string | null => {
  if (!val) return null;
  if (typeof val === 'string') return val.trim() || null;
  if (typeof val === 'object') {
    const v = val as { coluna?: string | null; quadro?: string | null };
    if (v.coluna && v.quadro) return `${v.quadro}: ${v.coluna}`;
    if (v.coluna || v.quadro) return v.coluna || v.quadro || null;
    return null; // objeto só com nulls não vira texto cru na tela
  }
  return String(val);
};

const extractKanbanStatusLabel = (val: unknown): string | null => {
  if (!val) return null;
  if (typeof val === 'object') {
    const coluna = (val as { coluna?: string | null }).coluna;
    return coluna?.trim() || null;
  }
  const label = String(val).trim();
  if (!label) return null;
  const separador = label.indexOf(':');
  return separador >= 0 ? label.slice(separador + 1).trim() || null : label;
};

type AjusteExibivel = { campo: string; anterior: unknown; novo: unknown };

const ROTULOS_AJUSTE: Record<string, string> = {
  produto: 'Produto',
  valor: 'Valor',
  valor_unit: 'Valor',
  cotacao: 'Cotação',
  numero_cotacao_operadora: 'Cotação',
  numero: 'Telefone',
  telefone: 'Telefone',
  total_pacote: 'Total do pacote',
  plano: 'Plano',
  quantidade: 'Quantidade',
  franquia_gb: 'Franquia',
};

const valorComparavel = (valor: unknown) => valor == null ? '' : String(valor).trim();

const formatarTelefoneTimeline = (valor: unknown) => {
  const digitos = valorComparavel(valor).replace(/\D/g, '');
  if (digitos.length === 11) return `(${digitos.slice(0, 2)}) ${digitos.slice(2, 7)}-${digitos.slice(7)}`;
  if (digitos.length === 10) return `(${digitos.slice(0, 2)}) ${digitos.slice(2, 6)}-${digitos.slice(6)}`;
  return valorComparavel(valor) || '(não informado)';
};

const rotuloAjuste = (campo: string) =>
  ROTULOS_AJUSTE[campo] || (/^linha_(\d+)$/.exec(campo) ? `Linha ${campo.slice(6)}` : campo);

const formatarValorAjuste = (campo: string, valor: unknown) => {
  if (valor == null || valor === '') return '(não informado)';
  if (campo === 'valor' || campo === 'valor_unit' || campo === 'total_pacote') {
    const numero = Number(valor);
    return Number.isFinite(numero)
      ? numero.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
      : String(valor);
  }
  if (campo === 'numero' || campo === 'telefone' || /^linha_\d+$/.test(campo)) return formatarTelefoneTimeline(valor);
  if (campo === 'franquia_gb') return `${valor} GB`;
  return String(valor);
};

const ajustesDoEvento = (item: TimelineEvent): AjusteExibivel[] => {
  const resultado = new Map<string, AjusteExibivel>();
  const mudancas = (item.para as { mudancas?: unknown } | null)?.mudancas;
  if (Array.isArray(mudancas)) {
    for (const mudanca of mudancas) {
      if (!mudanca || typeof mudanca !== 'object') continue;
      const valor = mudanca as { campo?: unknown; de?: unknown; para?: unknown };
      const campo = String(valor.campo || '').trim();
      if (!campo || valorComparavel(valor.de) === valorComparavel(valor.para)) continue;
      const chave = campo === 'valor_unit' ? 'valor' : campo === 'numero_cotacao_operadora' ? 'cotacao' : campo === 'numero' ? 'telefone' : campo;
      resultado.set(chave, { campo, anterior: valor.de, novo: valor.para });
    }
  }

  const alteracoes = (item.payload as { alteracoes?: unknown } | null)?.alteracoes;
  if (alteracoes && typeof alteracoes === 'object' && !Array.isArray(alteracoes)) {
    for (const [campo, dados] of Object.entries(alteracoes as Record<string, unknown>)) {
      if (!dados || typeof dados !== 'object' || Array.isArray(dados)) continue;
      const valores = dados as Record<string, unknown>;
      const anterior = valores.anterior_nome ?? valores.anterior;
      const novo = valores.novo_nome ?? valores.novo;
      if (valorComparavel(anterior) === valorComparavel(novo)) continue;
      const chave = campo === 'valor_unit' ? 'valor' : campo === 'numero_cotacao_operadora' ? 'cotacao' : campo;
      resultado.set(chave, { campo, anterior, novo });
    }
  }
  return [...resultado.values()];
};

const METODO_EMAIL_ROTULO: Record<string, string> = { smtp: 'SMTP', sendgrid: 'SendGrid', ambos: 'SMTP + SendGrid' };

/** Proposta enviada por e-mail: assunto, por onde saiu e o texto que o consultor escreveu (Douglas 05/10/2026). */
function PropostaEmailEnviada({ payload }: { payload: Record<string, unknown> }) {
  const [aberto, setAberto] = useState(false);
  const metodo = METODO_EMAIL_ROTULO[String(payload.metodo || 'sendgrid')] || 'SendGrid';
  const destinos = Array.isArray(payload.destinatarios) ? (payload.destinatarios as string[]) : [];
  const texto = [payload.abertura_texto, payload.fechamento_texto].filter(Boolean).map(String).join('\n\n');
  return (
    <div className="space-y-1">
      <p className="text-[11px] text-muted-foreground">
        por e-mail · {metodo}{destinos.length ? ` · ${destinos.join(', ')}` : ''}
      </p>
      {payload.assunto ? <p className="text-[11.5px] font-semibold leading-snug text-foreground">{String(payload.assunto)}</p> : null}
      {texto ? (
        <>
          <p className={cn('whitespace-pre-wrap rounded-lg border px-2.5 py-1.5 text-[11.5px] leading-snug text-foreground', !aberto && 'line-clamp-3')}
            style={{ backgroundColor: 'rgba(91,10,13,0.045)', borderColor: 'rgba(91,10,13,0.16)' }}>
            {texto}
          </p>
          <button type="button" onClick={() => setAberto((v) => !v)} className="text-[10.5px] font-semibold text-primary hover:underline">
            {aberto ? 'mostrar menos' : 'ver o texto inteiro'}
          </button>
        </>
      ) : null}
    </div>
  );
}

function LinkConversaExterna({ url }: { url?: string | null }) {
  if (!url) return null;
  return (
    <a href={url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-[10px] font-medium text-primary hover:underline">
      <ExternalLink className="h-2.5 w-2.5" /> Abrir conversa no WhatsApp
    </a>
  );
}

function LinkConversa({ conversaId, urlBee }: { conversaId?: string | null; urlBee?: string | null }) {
  if (conversaId) {
    return (
      <a href={`/whatsapp-inbox?conversation=${encodeURIComponent(conversaId)}`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-[10px] font-medium text-primary hover:underline">
        <ExternalLink className="h-2.5 w-2.5" /> Abrir conversa no WhatsApp
      </a>
    );
  }
  return <LinkConversaExterna url={urlBee} />;
}

function GravacaoLigacao({ gravacaoId, duracao }: { gravacaoId?: string | null; duracao?: number | null }) {
  const [url, setUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [erro, setErro] = useState<string | null>(null);

  if (!gravacaoId) return null;

  const abrir = async () => {
    setLoading(true);
    setErro(null);
    try {
      const { data } = await supabase.auth.getSession();
      const token = data.session?.access_token;
      if (!token) throw new Error('Sessão expirada');
      const response = await fetch(`${API_BASE}/v1/discadora/gravacoes/${gravacaoId}/url`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const payload = await response.json();
      if (!response.ok) throw new Error(payload?.error || 'Gravação indisponível');
      setUrl(payload.data.url);
    } catch (error) {
      setErro(error instanceof Error ? error.message : 'Gravação indisponível');
    } finally {
      setLoading(false);
    }
  };

  if (url) return <audio controls preload="none" src={url} className="mt-1 h-8 w-full max-w-[360px]" />;

  return (
    <div className="mt-1 flex items-center gap-2">
      <Button type="button" variant="outline" size="sm" className="h-7 gap-1.5 px-2 text-[10px]" onClick={abrir} disabled={loading}>
        {loading ? <Loader2 className="h-3 w-3 animate-spin" /> : <Play className="h-3 w-3" />}
        Ouvir gravação
      </Button>
      {duracao != null && <span className="text-[10px] tabular-nums text-muted-foreground">{duracao}s</span>}
      {erro && <span className="text-[10px] text-destructive">{erro}</span>}
    </div>
  );
}

export function TimelineUnificada({ cnpj, clienteId, pedidoId, triagemId, leadId, leadNumero, completa }: TimelineUnificadaProps) {
  const painelPedido = usePainelPedido();
  const [visibleCount, setVisibleCount] = useState(EVENTS_PER_PAGE);
  const [familiaFiltro, setFamiliaFiltro] = useState<FamiliaKey | null>(null);
  const cnpjLimpo = (cnpj || '').replace(/\D/g, '');
  const podeEnviarEmail = useHasAnyRole(['master', 'diretor', 'gerente', 'coordenador', 'supervisor', 'bko', 'qualidade']);
  const [emailLendo, setEmailLendo] = useState<string | null>(null);
  const [emailRespondendo, setEmailRespondendo] = useState<{ id: string; todos: boolean } | null>(null);
  const { data: conversaPrincipal } = useQuery<{ id: string } | null>({
    queryKey: ['timeline-conversa-principal', cnpjLimpo],
    enabled: cnpjLimpo.length === 14,
    staleTime: 2 * 60 * 1000,
    queryFn: async () => {
      const { data } = await supabase.auth.getSession();
      const token = data.session?.access_token;
      if (!token) return null;
      const response = await fetch(`${API_BASE}/v1/timeline/conversa-principal?cnpj=${encodeURIComponent(cnpjLimpo)}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) return null;
      const body = await response.json();
      return body?.data || null;
    },
  });

  const { data: allTimeline = [], isLoading, error } = useTimelineUnificada({ cnpj, clienteId, leadNumero, limit: 500 });

  // contagem por família — derivada do MESMO array (lei da régua dos números)
  const contagens = useMemo(() => {
    const c = new Map<FamiliaKey, number>();
    for (const ev of allTimeline) c.set(familiaDo(ev), (c.get(familiaDo(ev)) || 0) + 1);
    return c;
  }, [allTimeline]);

  const filtrada = useMemo(
    () => (familiaFiltro ? allTimeline.filter((ev) => familiaDo(ev) === familiaFiltro) : allTimeline),
    [allTimeline, familiaFiltro],
  );
  const timeline = filtrada.slice(0, visibleCount);
  const hasMore = filtrada.length > visibleCount;

  const groupedByDate = useMemo(() => {
    const groups: { label: string; key: string; events: TimelineEvent[] }[] = [];
    const map = new Map<string, TimelineEvent[]>();
    for (const event of timeline) {
      const d = toBrasilia(new Date(event.created_at));
      const key = format(d, 'yyyy-MM-dd');
      if (!map.has(key)) map.set(key, []);
      map.get(key)!.push(event);
    }
    for (const [key, events] of map) {
      const d = new Date(key + 'T12:00:00');
      let label: string;
      if (isToday(d)) label = 'Hoje';
      else if (isYesterday(d)) label = 'Ontem';
      else label = format(d, "dd 'de' MMMM 'de' yyyy · EEEE", { locale: ptBR });
      groups.push({ label, key, events });
    }
    return groups;
  }, [timeline]);

  // RESUMO — pares derivados dos eventos
  const resumo = useMemo(() => {
    if (allTimeline.length === 0) return null;
    const fmtQuando = (iso: string) => {
      const d = toBrasilia(new Date(iso));
      return isToday(d) ? `hoje ${format(d, 'HH:mm')}` : format(d, 'dd/MM/yyyy');
    };
    const criado = allTimeline.find((ev) => ev.tipo === 'criacao') || allTimeline[allTimeline.length - 1];
    const contato = allTimeline.find((ev) => ['ligacao', 'whatsapp'].includes(familiaDo(ev)));
    const proposta = allTimeline.find((ev) => ev.tipo === 'proposta');
    const faturas = allTimeline.filter((ev) => ev.tipo === 'fatura');
    return {
      criado: criado ? fmtQuando(criado.created_at) : '—',
      contato: contato ? fmtQuando(contato.created_at) : '—',
      proposta: proposta ? fmtQuando(proposta.created_at) : '—',
      faturas: faturas.length > 0 ? `${faturas.length} · última ${fmtQuando(faturas[0].created_at)}` : '—',
    };
  }, [allTimeline]);

  const renderCorpo = (item: TimelineEvent) => {
    if (item.tipo === 'troca_esim') {
      if (!item.para) return <p className="text-xs">{item.descricao}{item.motivo ? ` — ${item.motivo}` : ''}</p>;
      const linha = item.para as { telefone?: string; iccid?: string; revisao?: number; validada_em?: string; ativa?: boolean; foto_mensagem_id?: string; validacao_origem?: string } | null;
      return <div className="space-y-1 text-xs">
        <p>Linha: {linha?.telefone || 'Número novo'} · Revisão {linha?.revisao || '—'}</p>
        <p>ICCID: {linha?.iccid || 'Não conferido'}</p>
        <p>{linha?.ativa === false ? 'Linha arquivada' : linha?.validada_em ? `Validada (${linha.validacao_origem === 'master' ? 'exceção master' : linha.validacao_origem === 'operadora' ? 'troca concluída pela Claro' : 'cliente'})` : 'Aguardando validação'}</p>
        {linha?.foto_mensagem_id && <p className="text-muted-foreground">Foto vinculada; confira no painel de ativação do pedido.</p>}
        {item.motivo && <p className="text-muted-foreground">{item.motivo}</p>}
      </div>;
    }
    const payload = item.payload as Record<string, unknown> | null;
    const t = item.tipo || '';

    if (item.campo === 'ajuste_item') {
      const ajustes = ajustesDoEvento(item);
      const linhasRenovacao = Array.isArray((payload?.alteracoes as { renovacao_linhas?: unknown } | null)?.renovacao_linhas)
        ? (payload?.alteracoes as { renovacao_linhas: Array<{ telefone?: unknown; valor?: unknown }> }).renovacao_linhas
        : [];
      const itemSeq = (item.para as { item_seq?: unknown } | null)?.item_seq
        ?? (item.de as { item_seq?: unknown } | null)?.item_seq;
      const origemClaro = payload?.origem === 'Extrato Claro Control' || item.motivo === 'Conciliação com extrato Claro';
      const renovacao = linhasRenovacao.length > 0 || item.descricao?.startsWith('Pacote legado de renovação');
      const passaporte = item.descricao?.startsWith('Módulo Passaporte conciliado') === true;
      const produtoPassaporte = passaporte
        ? ((item.para as { produto?: unknown } | null)?.produto ?? (item.de as { produto?: unknown } | null)?.produto)
        : null;
      const itensPassaporte = passaporte && Array.isArray((item.para as { itens?: unknown } | null)?.itens)
        ? (item.para as { itens: Array<{ telefone?: unknown }> }).itens
        : [];
      const quantidadeAnteriorPassaporte = (item.de as { quantidade_itens?: unknown } | null)?.quantidade_itens;
      const alteracoesPayload = payload?.alteracoes && typeof payload.alteracoes === 'object'
        ? payload.alteracoes as Record<string, unknown>
        : {};
      const produtoPayload = alteracoesPayload.produto && typeof alteracoesPayload.produto === 'object'
        ? alteracoesPayload.produto as Record<string, unknown>
        : {};
      const produtoIdentificado = String(
        produtoPayload.novo_nome
        ?? produtoPayload.anterior_nome
        ?? (item.para as { produto?: unknown } | null)?.produto
        ?? (item.de as { produto?: unknown } | null)?.produto
        ?? '',
      ).trim();
      const camposAjustados = ajustes.map((ajuste) => rotuloAjuste(ajuste.campo));
      const correcaoNumeroLinha = payload?.acao === 'corrigir_numero_linha';
      const motivoAjuste = item.descricao?.startsWith('Fibra conciliada')
        ? 'Referência da instalação localizada no extrato; a FIBRA permanece sem telefone porque a Claro informa NTC 0.'
        : renovacao
          ? 'Quantidade, linhas e valores do pacote foram conferidos com o extrato Claro.'
          : passaporte
            ? 'Linhas do módulo financeiro foram conferidas com o plano pago no extrato Claro.'
            : camposAjustados.length > 0
              ? `${camposAjustados.join(', ')} ${camposAjustados.length === 1 ? 'divergia' : 'divergiam'} do pagamento identificado no extrato Claro.`
              : item.motivo || item.descricao || 'Item conferido com o extrato Claro.';
      return (
        <>
          <p className="text-[12.5px] font-semibold leading-snug text-foreground">
            {origemClaro ? 'Conciliação com extrato Claro' : correcaoNumeroLinha ? 'Correção do número da linha' : 'Correção do pedido'}{produtoIdentificado ? ` · ${produtoIdentificado}` : ''}{itemSeq ? ` · item ${itemSeq}` : ''}
          </p>
          {correcaoNumeroLinha && item.motivo && (
            <p className="text-[11px] leading-snug text-muted-foreground">
              <span className="font-medium text-foreground">Motivo:</span> {item.motivo}
            </p>
          )}
          {origemClaro && <p className="text-[10.5px] text-muted-foreground">Origem: Claro Control</p>}
          {origemClaro && (
            <p className="text-[11px] leading-snug text-muted-foreground">
              <span className="font-medium text-foreground">Por que foi ajustado:</span> {motivoAjuste}
            </p>
          )}
          {passaporte && (
            <div className="space-y-0.5 text-[11px] leading-snug text-muted-foreground">
              <p><span className="font-medium text-foreground">Produto:</span> <b className="font-semibold text-foreground">{String(produtoPassaporte || 'Passaporte')}</b></p>
              <p>
                <span className="font-medium text-foreground">Linhas vinculadas:</span>{' '}
                {String(quantidadeAnteriorPassaporte ?? 1)} <span className="text-muted-foreground/60">→</span>{' '}
                <b className="font-semibold text-foreground">{itensPassaporte.length}</b>
              </p>
            </div>
          )}
          {ajustes.length > 0 ? (
            <div className="space-y-0.5">
              {ajustes.map((ajuste) => (
                <p key={ajuste.campo} className="text-[11px] leading-snug text-muted-foreground">
                  <span className="font-medium text-foreground">{renovacao && ajuste.campo === 'valor_unit' ? 'Valor médio por linha' : rotuloAjuste(ajuste.campo)}:</span>{' '}
                  {formatarValorAjuste(ajuste.campo, ajuste.anterior)}{' '}
                  <span className="text-muted-foreground/60">→</span>{' '}
                  <b className="font-semibold text-foreground">{formatarValorAjuste(ajuste.campo, ajuste.novo)}</b>
                </p>
              ))}
            </div>
          ) : (
            <p className="text-[11px] leading-snug text-muted-foreground">{item.motivo || item.descricao || 'Item atualizado'}</p>
          )}
          {linhasRenovacao.length > 0 && (
            <div className="mt-1 rounded-md border border-border/70 bg-muted/30 px-2 py-1.5">
              <p className="mb-1 text-[10.5px] font-semibold text-foreground">{linhasRenovacao.length} linhas conciliadas</p>
              <div className="grid gap-x-3 gap-y-0.5 sm:grid-cols-2">
                {linhasRenovacao.map((linha, index) => (
                  <p key={`${String(linha.telefone || '')}-${index}`} className="flex justify-between gap-2 text-[10.5px] text-muted-foreground">
                    <span className="tabular-nums">{formatarTelefoneTimeline(linha.telefone)}</span>
                    <b className="font-semibold tabular-nums text-foreground">{formatarValorAjuste('valor', linha.valor)}</b>
                  </p>
                ))}
              </div>
            </div>
          )}
          {itensPassaporte.length > 0 && (
            <div className="mt-1 rounded-md border border-border/70 bg-muted/30 px-2 py-1.5">
              <p className="mb-1 text-[10.5px] font-semibold text-foreground">{itensPassaporte.length} linhas do Passaporte</p>
              <div className="grid gap-x-3 gap-y-0.5 sm:grid-cols-2">
                {itensPassaporte.map((linha, index) => (
                  <p key={`${String(linha.telefone || '')}-${index}`} className="text-[10.5px] tabular-nums text-muted-foreground">
                    {formatarTelefoneTimeline(linha.telefone)}
                  </p>
                ))}
              </div>
            </div>
          )}
        </>
      );
    }

    if (typeof item.descricao === 'string' && item.descricao.startsWith('Convergência')) {
      const desc = item.descricao;
      const reprovado = /REPROVADO/i.test(desc);
      const aprovado = /APROVAD/i.test(desc);
      const corpo = desc.replace(/^Convergência\s*(\([^)]*\))?\s*:\s*/i, '').trim() || desc;
      const quem = /\(Gerente/i.test(desc) || /pela gerente/i.test(desc) ? 'Gerente' : /\(Consultor\)/i.test(desc) || /enviado para a gerente/i.test(desc) ? 'Consultor' : null;
      return (
        <>
          <p className={cn('text-[12.5px] font-semibold leading-snug', reprovado ? 'text-primary dark:text-[#C87484]' : aprovado ? 'text-[#075E54] dark:text-[#25D366]' : 'text-foreground')}>
            <Wifi className="mr-1 inline h-3 w-3 align-[-1px]" />
            Justificativa de convergência{quem ? ` — ${quem}` : ''}{reprovado ? ' (reprovada)' : aprovado ? ' (aprovada)' : ''}
          </p>
          <p className="text-[11px] leading-snug text-muted-foreground">{corpo}</p>
        </>
      );
    }
    // ⛔ O `t !== 'email_recebido'` nao e' zelo: sem ele este bloco pega o
    // e-mail (que usa entidade 'mensagem') ANTES do bloco proprio dele la'
    // embaixo, e desenha o CORPO jogando fora o assunto — o inverso do certo.
    if ((item.entidade === 'mensagem' && !ehEmail(t)) || t === 'observacao' || t === 'triagem_nota' || t === 'interno') {
      const texto = item.entidade === 'mensagem'
        ? (item.motivo || item.descricao || 'Sem conteúdo')
        : ((payload?.observacao as string) || item.motivo || item.descricao || 'Observação');
      return (
        <p className="rounded-lg border px-2.5 py-1.5 text-[11.5px] italic leading-snug text-foreground"
          style={{ backgroundColor: 'rgba(91,10,13,0.045)', borderColor: 'rgba(91,10,13,0.16)' }}>
          “{texto}”
        </p>
      );
    }
    if (t === 'proposta') {
      return (
        <>
          <p className="text-[12.5px] font-semibold leading-snug text-foreground">{item.descricao || 'Proposta enviada'}</p>
          {(payload?.gb_novo || payload?.economia_mes) ? (
            <p className="text-[11px] text-muted-foreground">
              {payload?.gb_novo ? `Plano ${payload.gb_novo}GB` : ''}
              {payload?.economia_mes ? `${payload?.gb_novo ? ' · ' : ''}economia R$ ${Number(payload.economia_mes).toFixed(2).replace('.', ',')}/mês` : ''}
            </p>
          ) : null}
          {payload?.canal === 'email' ? <PropostaEmailEnviada payload={payload as Record<string, unknown>} /> : null}
          <LinkConversaExterna url={payload?.url_conversa as string | undefined} />
        </>
      );
    }
    if (t === 'pedido_ia') {
      const agente = String(payload?.agente_nome || 'Agente IA');
      const plano = String(payload?.plano || '').trim();
      const resumo = String(payload?.resumo || '').trim();
      const valor = Number(payload?.valor_total || 0);
      const status = String(payload?.status || '').trim();
      return (
        <>
          <p className="text-[12.5px] font-semibold leading-snug text-[#075E54] dark:text-[#25D366]">
            {agente} fechou e registrou este pedido
          </p>
          {(plano || resumo) && <p className="text-[11px] leading-snug text-muted-foreground">{[plano, resumo].filter(Boolean).join(' · ')}</p>}
          {valor > 0 && <p className="text-[11px] font-semibold text-foreground">{valor.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}/mês</p>}
          {status && <p className="text-[10.5px] text-muted-foreground">Situação inicial: {status}</p>}
          <LinkConversa conversaId={payload?.conversa_id as string | undefined} />
        </>
      );
    }
    if (t === 'tabulacao') {
      return (
        <>
          <p className="text-[12.5px] font-semibold leading-snug text-foreground">{item.descricao || 'Cliente tabulado'}</p>
          {item.motivo && item.motivo !== item.descricao ? <p className="text-[11px] leading-snug text-muted-foreground">{item.motivo}</p> : null}
        </>
      );
    }
    if (t === 'whatsapp' && payload?.origem === 'retencao_discadora') {
      const fase = String(payload?.fase || '');
      const resultado = String(payload?.resultado || '');
      return (
        <>
          <p className="text-[12.5px] font-semibold leading-snug text-foreground">{item.descricao || 'Movimento da retenção'}</p>
          {fase === 'disparo' ? <p className="text-[11px] leading-snug text-muted-foreground">Oferta automática enviada ao cliente.</p> : null}
          {fase === 'resposta' ? <p className="text-[11px] leading-snug text-muted-foreground">{resultado === 'recusado' ? 'Cliente respondeu sem interesse.' : 'Cliente respondeu com interesse; conversa liberada para atendimento.'}</p> : null}
          <LinkConversa conversaId={payload?.conversation_id as string | undefined} />
        </>
      );
    }
    if (t === 'status' || t === 'kanban_move' || t === 'campo' || t === 'agenda' || t === 'atribuicao') {
      const extractValue = t === 'status' || t === 'kanban_move' ? extractKanbanStatusLabel : extractLabel;
      const de = extractValue(item.de);
      const para = extractValue(item.para);
      return (
        <>
          <p className="text-[12.5px] font-semibold leading-snug text-foreground">{item.campo || item.descricao || rotuloDo(item)}</p>
          {de || para ? (
            <p className="truncate text-[11px] text-muted-foreground" title={`${de || '(vazio)'} → ${para || '(vazio)'}`}>
              {de || '(vazio)'} <span className="text-muted-foreground/60">→</span> <b className="font-semibold text-foreground">{para || '(vazio)'}</b>
            </p>
          ) : (item.motivo ? <p className="text-[11px] text-muted-foreground">{item.motivo}</p> : null)}
        </>
      );
    }
    if (ehEmail(t)) {
      const enviado = t === 'email_enviado';
      const deNome = (payload?.de_nome || '') as string;
      const deEmail = (payload?.de_email || '') as string;
      const caixa = (payload?.caixa || '') as string;
      const qtdAnexos = Number(payload?.qtd_anexos || 0);
      const agente = (payload?.agente || '') as string;
      // O e-mail da Venda IA CPF sai pelo SendGrid, não pela caixa: é automático.
      const automatico = payload?.origem === 'venda-ia-cpf';
      const quando = (iso: unknown) => (typeof iso === 'string' && iso ? format(toBrasilia(new Date(iso)), 'dd/MM HH:mm') : null);
      const entregue = quando(payload?.entregue_em);
      const aberto = quando(payload?.aberto_em);
      const naoEntregue = quando(payload?.nao_entregue_em);
      const paraLinha = Array.isArray(payload?.para)
        ? (payload.para as Array<{ nome?: string | null; email?: string | null }>)
            .map((d) => d?.nome || d?.email).filter(Boolean).join(', ')
        : '';
      // O corpo vem em `motivo` (600 caracteres). Aqui e' um relance: quem
      // mandou, sobre o que, e se tem anexo. Ler o e-mail inteiro nao e' papel
      // da timeline — e' do Outlook, que continua sendo onde se responde.
      return (
        <>
          <p className="text-[12.5px] font-semibold leading-snug text-foreground">{tituloEsim(item.descricao || '(sem assunto)')}</p>
          {enviado ? (
            <p className="truncate text-[11px] text-muted-foreground" title={`${caixa || deEmail} → ${paraLinha}`}>
              {!agente && deNome ? <><b className="font-semibold text-foreground">{deNome}</b><span className="text-muted-foreground/70"> · </span></> : null}
              <span className="text-muted-foreground/70">para </span>
              <b className="font-semibold text-foreground">{paraLinha || '(sem destinatário)'}</b>
              {automatico
                ? <span className="text-muted-foreground/60"> · automático da Venda IA CPF</span>
                : caixa ? <span className="text-muted-foreground/60"> · pela caixa {caixa}</span> : null}
              {agente ? <span className="text-muted-foreground/60"> · escrito pela IA ({agente})</span> : null}
            </p>
          ) : (
            <p className="truncate text-[11px] text-muted-foreground" title={`${deNome ? deNome + ' · ' : ''}${deEmail}${caixa ? ' → ' + caixa : ''}`}>
              <b className="font-semibold text-foreground">{deNome || deEmail}</b>
              {deNome && deEmail ? <span className="text-muted-foreground/70"> · {deEmail}</span> : null}
              {caixa ? <span className="text-muted-foreground/60"> → {caixa}</span> : null}
            </p>
          )}
          {item.motivo ? (
            <p className="text-[11px] italic leading-snug text-muted-foreground">
              “{item.motivo.substring(0, 160)}{item.motivo.length > 160 ? '…' : ''}”
            </p>
          ) : null}
          {qtdAnexos > 0 ? (
            <span className="inline-flex items-center gap-1 text-[10.5px] text-muted-foreground">
              <Paperclip className="h-2.5 w-2.5 shrink-0" />
              {qtdAnexos} {qtdAnexos === 1 ? 'anexo' : 'anexos'}
            </span>
          ) : null}
          {enviado && (entregue || aberto || naoEntregue) ? (
            <p className="text-[10.5px] text-muted-foreground">
              {naoEntregue
                ? <span className="text-destructive">não entregue {naoEntregue}{payload?.nao_entregue_motivo ? ` · ${payload.nao_entregue_motivo}` : ''}</span>
                : <>{entregue ? `entregue ${entregue}` : null}{entregue && aberto ? ' · ' : null}{aberto ? `aberto ${aberto}` : null}</>}
            </p>
          ) : null}
          {podeEnviarEmail ? (
            <div className="flex flex-wrap items-center gap-1.5 pt-1">
              {cnpjLimpo.length === 14 ? (
                <>
                  <button type="button" onClick={() => setEmailRespondendo({ id: item.id, todos: false })}
                    className="inline-flex h-6 items-center gap-1 rounded-md border border-border px-2 text-[10.5px] font-semibold text-muted-foreground hover:border-primary/50 hover:text-primary">
                    <Reply className="h-3 w-3" />Responder
                  </button>
                  <button type="button" onClick={() => setEmailRespondendo({ id: item.id, todos: true })}
                    className="inline-flex h-6 items-center gap-1 rounded-md border border-border px-2 text-[10.5px] font-semibold text-muted-foreground hover:border-primary/50 hover:text-primary">
                    <ReplyAll className="h-3 w-3" />Responder a todos
                  </button>
                </>
              ) : null}
              <button type="button" onClick={() => setEmailLendo(item.id)}
                className="inline-flex h-6 items-center gap-1 rounded-md px-2 text-[10.5px] font-semibold text-muted-foreground hover:text-primary">
                <Eye className="h-3 w-3" />Ver completo
              </button>
            </div>
          ) : null}
        </>
      );
    }
    if (t === 'biometria') {
      const link = payload?.link as string | undefined;
      return (
        <>
          <p className="text-[12.5px] font-semibold leading-snug text-foreground">Link de biometria {payload?.tentativa ? `· tentativa ${payload.tentativa}` : ''}</p>
          {link && (
            <a href={link} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 break-all text-[10px] text-primary hover:underline" title={link}>
              <ExternalLink className="h-2.5 w-2.5 shrink-0" />{link}
            </a>
          )}
        </>
      );
    }
    if (t === 'qualidade_whatsapp') {
      const texto = (payload?.texto || payload?.mensagem) as string | undefined;
      return (
        <>
          <p className="text-[12.5px] font-semibold leading-snug text-foreground">Mensagem da Qualidade enviada</p>
          {(payload?.telefone || payload?.numero_destino) ? <p className="text-[10.5px] tabular-nums text-muted-foreground">{String(payload?.telefone || payload?.numero_destino)}</p> : null}
          {texto && <p className="text-[11px] italic leading-snug text-muted-foreground">“{texto.substring(0, 120)}{texto.length > 120 ? '…' : ''}”</p>}
          <LinkConversa conversaId={conversaPrincipal?.id} urlBee={payload?.url_conversa_bee as string | undefined} />
        </>
      );
    }
    if (t.startsWith('qualidade_')) {
      const nota = payload?.nota_atendimento as number | null | undefined;
      const obs = payload?.observacao_conclusao as string | null | undefined;
      const nomeContato = payload?.nome_contato as string | null | undefined;
      const canal = payload?.canal_contato as string | null | undefined;
      const canalLabel = canal === 'whatsapp' ? 'WhatsApp' : canal === 'email' ? 'E-mail' : canal === 'telefone' ? 'Telefone' : canal;
      return (
        <>
          <p className="text-[12.5px] font-semibold leading-snug text-foreground">{obs || item.descricao || rotuloDo(item)}</p>
          {nota != null && nota > 0 && (
            <span className="flex items-center gap-0.5">
              {[1, 2, 3, 4, 5].map((s2) => (
                <Star key={s2} className={cn('h-3 w-3', s2 <= nota ? 'fill-warning text-warning' : 'fill-muted text-muted-foreground/20')} />
              ))}
              <span className="ml-1 text-[10px] text-muted-foreground">{nota} de 5</span>
            </span>
          )}
          {nomeContato && <p className="text-[10.5px] text-muted-foreground">contato: {nomeContato}{canalLabel ? ` via ${canalLabel}` : ''}</p>}
          <LinkConversa conversaId={conversaPrincipal?.id} urlBee={payload?.url_conversa_bee as string | undefined} />
        </>
      );
    }
    // O motivo da Claro e a defesa da Prime são coisas diferentes e ficam
    // separados: é daqui que a IsabellA tira o motivo REAL do reprovado.
    if (t === 'defesa_credito') {
      const motivoClaro = payload?.motivo_claro as string | null | undefined;
      const defesaPrime = payload?.defesa_prime as string | null | undefined;
      const numeroDefesa = payload?.numero_defesa as string | null | undefined;
      const cotacao = payload?.numero_cotacao as string | null | undefined;
      return (
        <>
          <p className="text-[12.5px] font-semibold leading-snug text-foreground">
            {item.descricao || 'Crédito reprovado'}
          </p>
          {(numeroDefesa || item.campo) && (
            <p className="text-[10.5px] tabular-nums text-muted-foreground">
              defesa {numeroDefesa || item.campo}{cotacao ? ` · cotação ${cotacao}` : ''}
            </p>
          )}
          <p className="text-[11px] leading-snug text-muted-foreground">
            <span className="font-medium text-foreground">Motivo da Claro:</span>{' '}
            {motivoClaro?.trim() || 'não cadastrado'}
          </p>
          <p className="text-[11px] leading-snug text-muted-foreground">
            <span className="font-medium text-foreground">Defesa da Prime:</span>{' '}
            {defesaPrime?.trim() || 'não cadastrada'}
          </p>
        </>
      );
    }
    if (t === 'triagem_atualizada_ia') {
      return (
        <>
          <p className="text-[12.5px] font-semibold leading-snug text-foreground">{item.descricao || 'Atualizado pela IA'}</p>
          {payload?.motivo_resumo ? <p className="text-[11px] italic leading-snug text-muted-foreground">“{String(payload.motivo_resumo)}”</p> : null}
          <LinkConversa conversaId={conversaPrincipal?.id} urlBee={payload?.conversation_link as string | undefined} />
        </>
      );
    }
    if (t.startsWith('triagem_')) {
      return (
        <>
          <p className="text-[12.5px] font-semibold leading-snug text-foreground">{item.descricao || rotuloDo(item)}</p>
          {item.motivo && <p className="text-[11px] leading-snug text-muted-foreground line-clamp-2">{item.motivo}</p>}
          <LinkConversa conversaId={conversaPrincipal?.id} urlBee={payload?.conversation_link as string | undefined} />
        </>
      );
    }
    if (t === 'ligacao') {
      const gravacaoId = payload?.gravacao_id as string | null | undefined;
      const duracao = payload?.duracao_segundos == null ? null : Number(payload.duracao_segundos);
      return (
        <>
          <p className="text-[12.5px] font-semibold leading-snug text-foreground">{item.descricao || 'Ligação realizada'}</p>
          {payload?.numero ? <p className="text-[10.5px] tabular-nums text-muted-foreground">{String(payload.numero)}</p> : null}
          <GravacaoLigacao gravacaoId={gravacaoId} duracao={duracao} />
        </>
      );
    }
    if (t === 'tentativa_ligacao') {
      const status = String(payload?.status || 'INICIADA');
      const statusLabel: Record<string, string> = {
        INICIADA: 'Chamada iniciada',
        ATENDIDA: 'Atendida',
        NAO_ATENDIDA: 'Não atendida',
        OCUPADO: 'Ocupado',
        FALHOU: 'Não completada',
      };
      const gravacaoId = payload?.gravacao_id as string | null | undefined;
      const duracao = payload?.duracao_segundos == null ? null : Number(payload.duracao_segundos);
      const tentados = payload?.origem === 'auto_dialer' && Array.isArray(payload?.numeros)
        ? (payload.numeros as Array<{ numero?: string; rotulo?: string | null; resultado?: string }>)
        : null;
      if (tentados) {
        const resultado: Record<string, string> = {
          atendeu: 'atendeu', caixa_postal: 'caixa postal', nao_atende: 'não atendeu', ocupado: 'ocupado', invalido: 'número inválido', erro: 'não completou',
        };
        return (
          <>
            <p className="text-[12.5px] font-semibold leading-snug text-foreground">{item.descricao || 'Auto Dialer'}</p>
            {tentados.map((n, i) => (
              <p key={i} className="text-[10.5px] tabular-nums text-muted-foreground">
                {n.numero || '—'}{n.rotulo ? ` · ${n.rotulo}` : ''} · <span className={n.resultado === 'atendeu' ? 'font-semibold text-foreground' : ''}>{resultado[String(n.resultado)] || n.resultado}</span>
              </p>
            ))}
          </>
        );
      }
      return (
        <>
          <p className="text-[12.5px] font-semibold leading-snug text-foreground">{item.descricao || statusLabel[status] || 'Tentativa de ligação'}</p>
          <p className="text-[10.5px] tabular-nums text-muted-foreground">
            {payload?.numero || payload?.telefone ? String(payload?.numero || payload?.telefone) : 'Número não identificado'} · {statusLabel[status] || status}
          </p>
          {duracao != null && duracao >= 30 && <GravacaoLigacao gravacaoId={gravacaoId} duracao={duracao} />}
        </>
      );
    }
    // TAREFA — o título é o que ficou de fazer; o subtítulo diz o prazo, o
    // atraso e o adiamento. É a linha que o supervisor lê sem abrir nada.
    if (t === 'agenda') {
      const campo = String(item.campo || '');
      const cumprida = campo === 'Tarefa cumprida';
      const adiada = campo === 'Tarefa adiada';
      const quando = (() => {
        const bruto = typeof item.para === 'string' ? item.para : (item.para as { toString?: () => string })?.toString?.();
        const d = bruto ? new Date(String(bruto).replace(/^"|"$/g, '')) : null;
        return d && !Number.isNaN(d.getTime())
          ? d.toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })
          : null;
      })();
      const atrasada = !!(payload && (payload as Record<string, unknown>).atrasada);
      const vezes = Number((payload as Record<string, unknown> | null)?.vezes ?? 0);
      const sub = cumprida
        ? [atrasada ? 'fora do prazo' : 'no prazo', item.para ? `“${String(item.para).replace(/^"|"$/g, '')}”` : null]
            .filter(Boolean).join(' · ')
        : adiada
          ? [quando ? `adiada para ${quando}` : 'adiada', vezes ? `${vezes}ª vez` : null, item.motivo ? `“${item.motivo}”` : null]
              .filter(Boolean).join(' · ')
          : quando ? `para ${quando}` : null;
      return (
        <>
          <p className={cn('text-[12.5px] font-semibold leading-snug',
            cumprida ? 'text-[#075E54] dark:text-[#25D366]' : 'text-foreground')}>
            {item.descricao || rotuloDo(item)}
          </p>
          {sub && <p className="text-[11px] leading-snug text-muted-foreground line-clamp-2">{sub}</p>}
        </>
      );
    }
    // VENDA FECHADA PELA IA (16/09/2026) — o resumo inteiro fica aqui, sem corte:
    // e' com ele que o consultor abre o pedido. Cortar em duas linhas devolveria
    // a pessoa para a conversa, que e' exatamente o que a timeline evita.
    if (t === 'venda_ia') {
      const linhas = String(item.motivo || '').split('\n').map((l) => l.trim()).filter(Boolean);
      const total = Number((payload?.valor_total_mensal as number) ?? 0);
      return (
        <>
          <p className="text-[12.5px] font-semibold leading-snug text-[#075E54] dark:text-[#25D366]">
            {item.descricao || 'Venda fechada pela IA'}
          </p>
          {linhas.length > 0 && (
            <ul className="mt-0.5 space-y-0.5">
              {linhas.map((linha, i) => (
                <li key={i} className="text-[11px] leading-snug text-muted-foreground">• {linha.replace(/^[•-]\s*/, '')}</li>
              ))}
            </ul>
          )}
          {total > 0 && (
            <p className="mt-0.5 text-[11px] font-semibold leading-snug text-foreground">
              Total {total.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}/mês
            </p>
          )}
          <p className="mt-0.5 text-[10px] uppercase tracking-[0.06em] text-muted-foreground">Pedido ainda não registrado</p>
        </>
      );
    }
    const destaque = t === 'cancelamento' ? 'text-primary dark:text-[#C87484]' : t === 'recuperacao' || t === 'conversao' ? 'text-[#075E54] dark:text-[#25D366]' : 'text-foreground';
    return (
      <>
        <p className={cn('text-[12.5px] font-semibold leading-snug', destaque)}>{item.descricao || item.motivo || rotuloDo(item)}</p>
        {item.descricao && item.motivo && item.descricao !== item.motivo && (
          <p className="text-[11px] leading-snug text-muted-foreground line-clamp-2">{item.motivo}</p>
        )}
      </>
    );
  };

  const renderEvento = (item: TimelineEvent) => {
    const fam = FAMILIAS[familiaDo(item)];
    const ICONE_FAMILIA: Record<FamiliaKey, ComponentType<{ className?: string; style?: import('react').CSSProperties }>> = {
      nota: StickyNote, ligacao: Phone, whatsapp: MessageSquare, email: Mail, fatura: FileText, proposta: Send, tabulacao: ClipboardCheck, tarefa: Calendar, atendimento: Star, troca_esim: ShieldCheck, sistema: Clock,
    };
    const Icone = ICONE_TIPO[item.tipo || ''] || ICONE_FAMILIA[familiaDo(item)] || Clock;
    const eventDate = toBrasilia(new Date(item.created_at));
    const hora = format(eventDate, 'HH:mm');
    const dataCompleta = format(eventDate, 'dd/MM/yyyy HH:mm', { locale: ptBR });
    const autor = item.tipo === 'pedido_ia' || item.tipo === 'venda_ia'
      ? String((item.payload as Record<string, unknown> | null)?.agente_nome || 'Agente IA')
      : item.user_nome || 'Sistema';
    const numero = item.entidade === 'pedido' && item.numero_pedido ? `#${item.numero_pedido}` : null;
    return (
      <div key={item.id} className="relative py-2 pl-10 pr-1">
        <span
          className="absolute left-0 top-2 grid h-[26px] w-[26px] place-items-center overflow-hidden rounded-full bg-card"
          style={{ border: `1px solid ${hexRgba(fam.cor, 0.5)}` }}
        >
          <span aria-hidden className="absolute inset-0" style={{ backgroundColor: hexRgba(fam.cor, 0.15) }} />
          <Icone className="relative h-3 w-3" style={{ color: fam.cor }} />
        </span>
        <div className="flex items-baseline gap-1.5">
          <span className="text-[9px] font-bold uppercase tracking-[0.08em]" style={{ color: fam.corTexto }}>{rotuloDo(item)}</span>
          {item.pedido_id && <button type="button" className="font-mono text-[9.5px] text-primary hover:underline" onClick={() => painelPedido.open(item.pedido_id!, 'visao360')}>{numero || 'Abrir pedido'}</button>}
          <span className="truncate text-[10px] text-muted-foreground/70">· {autor}</span>
          {/* Nota histórica do atendimento (eventos anteriores a 21/09/2026). */}
          {item.tipo === 'atendimento' && (item.payload as Record<string, unknown> | null)?.nota != null && (
            <span
              className="shrink-0 rounded-[4px] border px-[5px] text-[9.5px] font-bold leading-[14px] tabular-nums"
              style={{ borderColor: hexRgba(fam.cor, 0.45), color: fam.corTexto }}
              title="Nota deste atendimento (0 a 5)"
            >
              {Number((item.payload as Record<string, number>).nota).toFixed(1)}/5
            </span>
          )}
          <span className="ml-auto shrink-0 text-[10.5px] tabular-nums text-muted-foreground" title={dataCompleta}>{hora}</span>
        </div>
        <div className="mt-0.5 space-y-1">{renderCorpo(item)}</div>
      </div>
    );
  };

  if (error) {
    return <p className="text-xs text-destructive">Erro ao carregar timeline: {(error as Error).message}</p>;
  }

  const cardTimeline = (
    <div className="relative overflow-hidden rounded-xl border border-border bg-card pl-1">
      <span aria-hidden className="absolute inset-y-0 left-0 w-1 bg-[linear-gradient(180deg,#6E1626,#4E1522_55%,#3E0E1B)]" />
      <div className="flex items-center justify-between gap-3 border-b border-border px-4 py-2">
        <p className="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-[0.08em] text-foreground"><Clock className="h-3.5 w-3.5 text-muted-foreground" />Timeline do cliente</p>
        <p className="text-[10px] text-muted-foreground">{filtrada.length} evento{filtrada.length === 1 ? '' : 's'} · mais recente primeiro</p>
      </div>
      <div className="px-4 py-3">
        {isLoading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
            <span className="ml-2 text-xs text-muted-foreground">Carregando…</span>
          </div>
        ) : timeline.length > 0 ? (
          <div className="relative">
            {/* o fio */}
            <span aria-hidden className="absolute bottom-1 left-[12px] top-1 w-[2px] rounded bg-border" />
            {groupedByDate.map((group) => (
              <div key={group.key}>
                {/* dia como marcador NO fio */}
                <div className="relative flex items-center gap-2 py-1.5 pl-10">
                  <span className="absolute left-0 inline-flex h-5 items-center rounded-full border border-primary bg-primary px-2 text-[9px] font-bold uppercase tracking-[0.08em] text-primary-foreground shadow-sm" style={{ left: '-2px' }}>
                    {group.label}
                  </span>
                  <span className="h-px flex-1 bg-border/60" />
                </div>
                {group.events.map(renderEvento)}
              </div>
            ))}
            {hasMore && (
              <div className="flex justify-center pt-2">
                <Button variant="outline" size="sm" onClick={() => setVisibleCount((prev) => prev + EVENTS_PER_PAGE)} className="h-7 gap-1.5 text-xs">
                  <ChevronDown className="h-3.5 w-3.5" />
                  Carregar mais ({filtrada.length - visibleCount} restantes)
                </Button>
              </div>
            )}
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center py-8 text-center">
            <div className="mb-3 rounded-full bg-muted p-3"><Clock className="h-6 w-6 text-muted-foreground opacity-50" /></div>
            <p className="text-xs text-muted-foreground">{familiaFiltro ? `Nenhum evento de ${FAMILIAS[familiaFiltro].label} ainda` : 'Nenhum evento registrado ainda'}</p>
          </div>
        )}
      </div>
      {completa && (
        <div className="border-t border-border bg-[#FAF8F4] px-4 py-1.5 text-[10px] text-muted-foreground dark:bg-[#241A1A]">
          a nota do simulador e as propostas enviadas entram aqui sozinhas
        </div>
      )}
    </div>
  );

  const chipsFiltro = (
    <div className="flex flex-wrap gap-1.5">
      <button type="button" onClick={() => setFamiliaFiltro(null)}
        className={cn('flex h-[26px] items-center gap-1.5 rounded-full border px-2.5 text-[11px] font-semibold transition-colors',
          familiaFiltro === null ? 'border-transparent bg-[linear-gradient(90deg,#4A1320,#5B0A0D)] text-white' : 'border-border bg-card text-muted-foreground hover:text-foreground')}>
        Todos {allTimeline.length}
      </button>
      {(Object.keys(FAMILIAS) as FamiliaKey[]).filter((f) => (contagens.get(f) || 0) > 0).map((f) => (
        <button key={f} type="button" onClick={() => setFamiliaFiltro((cur) => (cur === f ? null : f))}
          className={cn('flex h-[26px] items-center gap-1.5 rounded-full border px-2.5 text-[11px] font-semibold transition-colors',
            familiaFiltro === f ? 'border-primary/50 bg-primary/5 text-foreground' : 'border-border bg-card text-muted-foreground hover:text-foreground')}>
          <span className="h-2 w-2 rounded-full" style={{ backgroundColor: FAMILIAS[f].cor }} />
          {FAMILIAS[f].label} {contagens.get(f)}
        </button>
      ))}
    </div>
  );

  const dialogsEmail = (
    <>
      {emailLendo && (
        <EmailLeituraDialog mensagemId={emailLendo} open={!!emailLendo} onOpenChange={(o) => { if (!o) setEmailLendo(null); }}
          onResponder={(todos) => { const id = emailLendo; setEmailLendo(null); setEmailRespondendo({ id, todos }); }} />
      )}
      {emailRespondendo && (
        <EmailClienteDialog open={!!emailRespondendo} onOpenChange={(o) => { if (!o) setEmailRespondendo(null); }}
          modo={{ tipo: 'responder', mensagemId: emailRespondendo.id, todos: emailRespondendo.todos, cnpj: cnpjLimpo, pedidoId: pedidoId ?? null }} />
      )}
    </>
  );

  if (!completa) {
    return (
      <div className="space-y-2">
        {dialogsEmail}
        <NotaRapida clienteId={clienteId} pedidoId={pedidoId} triagemId={triagemId} leadId={leadId} cnpj={cnpj || undefined} />
        {allTimeline.length > 0 && chipsFiltro}
        {cardTimeline}
      </div>
    );
  }

  return (
    <div className="grid items-start gap-4 lg:grid-cols-[minmax(0,1fr)_316px]">
      {dialogsEmail}
      <div className="space-y-3">{cardTimeline}</div>
      <div className="space-y-3">
        <NotaRapida clienteId={clienteId} pedidoId={pedidoId} triagemId={triagemId} leadId={leadId} cnpj={cnpj || undefined} />
        <div className="rounded-xl border border-border bg-card">
          <p className="border-b border-border px-3.5 py-2 text-[11px] font-bold uppercase tracking-[0.08em] text-foreground">Filtrar por tipo</p>
          <div className="p-3">{chipsFiltro}</div>
        </div>
        {resumo && (
          <div className="rounded-xl border border-border bg-card">
            <p className="flex items-center gap-1.5 border-b border-border px-3.5 py-2 text-[11px] font-bold uppercase tracking-[0.08em] text-foreground"><Clock className="h-3.5 w-3.5 text-muted-foreground" />Resumo</p>
            <div className="space-y-1.5 p-3.5 text-[12px]">
              {[
                ['Lead criado', resumo.criado],
                ['Último contato', resumo.contato],
                ['Última proposta', resumo.proposta],
                ['Faturas recebidas', resumo.faturas],
              ].map(([rotulo, valor]) => (
                <div key={rotulo} className="flex items-baseline justify-between gap-3">
                  <span className="text-muted-foreground">{rotulo}</span>
                  <span className="text-right font-semibold tabular-nums text-foreground">{valor}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
